/**
 * WEB-1139 (П1): приём `?adapty_profile_id=` / `?revenuecat_profile_id=` на
 * публичных страницах (вариант Б, решение PM — как web2wave).
 *
 * Приложение клиента открывает наш квиз/пейвол в WebView, дописав в URL свой
 * profile-id из SDK Adapty/RevenueCat. Мы сохраняем пару guid↔profile-id на
 * сервере — по ней выдаётся доступ после покупки.
 *
 * Устойчивость к редиректам чекаута (AC-1): параметры складываются в
 * долгоживущее хранилище при первом заходе, отправка идёт оттуда — вернувшийся
 * после оплаты URL уже без параметров, но связка не теряется. Отправка
 * fire-and-forget, повтор безвреден (upsert на сервере).
 *
 * WEB-1165 — память переживает закрытие вкладки (приём WEB-1436, зеркало
 * qb_utm_lt/qb_utm_ft). Раньше связка жила в sessionStorage и умирала со
 * вкладкой: заход из встроенного браузера приложения со ВТОРЫМ идентификатором
 * в новой вкладке не мог предъявить первый — дозапись второй платформы
 * отвергалась навсегда (мониторинг: причина `no-proof`, WEB-1158/1163).
 * Теперь хранилище — кука `qb_provider_profiles`, TTL 400 дней (потолок
 * Chrome, как `qb_utm_ft`/`_ft_click_id`: идентификатор установки приложения —
 * долгоживущая сущность). Кука host-only (без Domain) — воронки разных
 * проектов на разных хостах не видят чужих связок.
 *
 * Обратная совместимость: legacy-ключ sessionStorage читается, когда куки ещё
 * нет (визит, начавшийся до релиза), и мигрирует в куку при первой же записи.
 * Больше в sessionStorage НЕ пишем. Смена профиля не «замораживается»: свежее
 * значение из URL по-прежнему перебивает сохранённое (переустановка = новый
 * профиль), кука — не write-once.
 */
import { API_BASE_URL } from '@/config/api-url';
import {
  APP_GUID_MAX_LENGTH,
  APP_GUID_MIN_LENGTH,
  isAppGuidLengthValid,
  resolveAppGuid,
} from '@/utils/appGuid';

/** Legacy sessionStorage-ключ (до WEB-1165). Только чтение + миграция. */
const STORAGE_KEY = 'qb_provider_profiles';

/** Долгоживущее хранилище связки (JSON StoredProfiles). */
export const PROVIDER_PROFILES_COOKIE = 'qb_provider_profiles';
const PROVIDER_PROFILES_TTL_DAYS = 400;

interface StoredProfiles {
  adaptyProfileId?: string;
  revenuecatProfileId?: string;
  /**
   * Ключ «уже отправлено» — пара «projectId:guid» (не спамим при каждом
   * заходе). Ревизия WEB-1139: раньше хранился только projectId, поэтому смена
   * guid (сперва веб-заход под visitor-id, потом открытие из приложения под
   * app-guid) не вызывала переотправку — связка оставалась только под старым
   * guid, а покупка шла под новым, и доступ молча не выдавался. Старое значение
   * из sessionStorage просто не совпадёт с новым ключом → безопасная
   * переотправка (upsert на сервере идемпотентен).
   */
  sentFor?: string;
}

/** Ключ отправленности: связка привязана и к проекту, и к guid. */
function sentKey(projectId: string, guid: string): string {
  return `${projectId}:${guid}`;
}

function readCookie(name: string): string | undefined {
  try {
    const match = document.cookie.match(
      new RegExp('(?:^|; )' + name + '=([^;]*)'),
    );
    return match ? decodeURIComponent(match[1]) : undefined;
  } catch {
    return undefined;
  }
}

function writeCookie(name: string, value: string, days: number): void {
  try {
    const expires = new Date(Date.now() + days * 86_400_000).toUTCString();
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax${secure}`;
  } catch {
    // приватный режим/запрет кук — просто отправим то, что в URL, без персиста
  }
}

function sane(v: string | null): string | undefined {
  const trimmed = v?.trim();
  return trimmed && trimmed.length <= 255 ? trimmed : undefined;
}

/** Разбор JSON хранилища с поверкой типов по полям (мусор — мимо). */
function parseStored(raw: string | null | undefined): StoredProfiles {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (typeof parsed !== 'object' || parsed === null) return {};
    const pick = (key: keyof StoredProfiles): string | undefined => {
      const v = parsed[key];
      return typeof v === 'string' ? sane(v) : undefined;
    };
    return {
      adaptyProfileId: pick('adaptyProfileId'),
      revenuecatProfileId: pick('revenuecatProfileId'),
      sentFor: pick('sentFor'),
    };
  } catch {
    return {};
  }
}

function hasAny(v: StoredProfiles): boolean {
  return Boolean(v.adaptyProfileId || v.revenuecatProfileId || v.sentFor);
}

function readStored(): StoredProfiles {
  const fromCookie = parseStored(readCookie(PROVIDER_PROFILES_COOKIE));
  if (hasAny(fromCookie)) return fromCookie;
  // Legacy sessionStorage (визит, начавшийся до релиза WEB-1165): читаем,
  // миграция в куку происходит при первой же записи (writeStored ниже).
  try {
    return parseStored(sessionStorage.getItem(STORAGE_KEY));
  } catch {
    return {};
  }
}

function writeStored(v: StoredProfiles): void {
  writeCookie(
    PROVIDER_PROFILES_COOKIE,
    JSON.stringify(v),
    PROVIDER_PROFILES_TTL_DAYS,
  );
}

/**
 * Вызвать при монтировании публичной страницы (квиз/пейвол). Читает URL,
 * мёрджит с сохранённым, шлёт связку на сервер, если она ещё не отправлена
 * для этого проекта. Ошибки сети молча глотаются — страница важнее связки.
 */
export function captureAndLinkProviderProfiles(projectId: string): void {
  // До ранних return'ов: резолв заодно персистит app-guid из URL — иначе
  // заход «origin=app&guid=X без profile-id» не сохранил бы guid для
  // последующих страниц без query.
  const guid = resolveAppGuid();
  let fromUrl: { adapty?: string; revenuecat?: string } = {};
  try {
    const params = new URLSearchParams(window.location.search);
    fromUrl = {
      adapty: sane(params.get('adapty_profile_id')),
      revenuecat: sane(params.get('revenuecat_profile_id')),
    };
  } catch {
    // SSR/битый URL — работаем только с сохранённым
  }

  const stored = readStored();
  const merged: StoredProfiles = {
    adaptyProfileId: fromUrl.adapty ?? stored.adaptyProfileId,
    revenuecatProfileId: fromUrl.revenuecat ?? stored.revenuecatProfileId,
    sentFor: stored.sentFor,
  };
  if (!merged.adaptyProfileId && !merged.revenuecatProfileId) return;

  // Новые значения из URL перебивают отправленность (переустановка = новый профиль).
  const hasFresh =
    (fromUrl.adapty && fromUrl.adapty !== stored.adaptyProfileId) ||
    (fromUrl.revenuecat && fromUrl.revenuecat !== stored.revenuecatProfileId);
  if (merged.sentFor === sentKey(projectId, guid) && !hasFresh) {
    writeStored(merged);
    return;
  }

  writeStored(merged);

  // WEB-1888 (п.5): guid короче общей грамматики публичных дверей сервер
  // отвергнет (400), и связка не появится — при том, что ОПЛАТА такой
  // идентификатор принимает. Отказ ниже глушится намеренно (страница важнее
  // связки), поэтому причину говорим здесь: без этой строки интегратор видит
  // только «оплатил, а доступа нет». Запрос всё равно отправляем — на сервере
  // такой отказ поднимается в наблюдение (ProviderLinkFormatMonitorGuard), а
  // отменённый запрос не увидел бы никто.
  if (!isAppGuidLengthValid(guid)) {
    // eslint-disable-next-line no-console
    console.warn(
      // eslint-disable-next-line max-len
      `[provider-link] идентификатор посетителя длиной ${guid.length} символов не укладывается в допустимые ${APP_GUID_MIN_LENGTH}–${APP_GUID_MAX_LENGTH} — сервер отклонит связку покупателя с профилем платформы, и доступ в приложении не появится. Передавайте один и тот же идентификатор и в оплату, и в связку.`, // i18n-allow: сообщение в консоль разработчику интеграции, а не надпись кабинета
    );
  }

  void fetch(`${API_BASE_URL}/public/provider-link/${projectId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      guid,
      ...(merged.adaptyProfileId
        ? { adaptyProfileId: merged.adaptyProfileId }
        : {}),
      ...(merged.revenuecatProfileId
        ? { revenuecatProfileId: merged.revenuecatProfileId }
        : {}),
    }),
    keepalive: true,
  })
    .then((res) => {
      if (res.ok) writeStored({ ...merged, sentFor: sentKey(projectId, guid) });
    })
    .catch(() => {
      // молча — следующий заход повторит
    });
}
